import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, FileText, ListChecks, Loader2, Save, Sparkles, Target, Trash2 } from 'lucide-react'
import { ClassUpdateInputSchema } from '@sophia/shared-schemas'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Tooltip } from '@/components/ui/Tooltip'
import { classesApi, classesQueryKey } from '../api'
import { StatusBadge } from '../components/StatusBadge'
import { SignalsEditor } from '../components/SignalsEditor'
import { GoldExamplesEditor } from '../components/GoldExamplesEditor'
import { GenerationPanel } from '../components/GenerationPanel'
import { ClassPerformanceTab } from '../components/ClassPerformanceTab'
import { classMaturity } from '../lib/class-maturity'
import { testSetsApi, testSetsQueryKey } from '@/features/test-sets/api'
import { extractApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

type Tab = 'overview' | 'signals' | 'examples' | 'generation' | 'performance'

const TABS: { id: Tab; label: string; countKey?: 'signals' | 'examples' | 'generation' }[] = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'signals', label: 'Sinais', countKey: 'signals' },
  { id: 'examples', label: 'Exemplos-padrão', countKey: 'examples' },
  { id: 'generation', label: 'Regex & geração', countKey: 'generation' },
  { id: 'performance', label: 'Performance' },
]

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `${(v * 100).toFixed(1)}%`
}

export function ClassDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('overview')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: classesQueryKey.detail(id!),
    queryFn: () => classesApi.detail(id!),
    enabled: !!id,
  })

  const removeMut = useMutation({
    mutationFn: () => classesApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classesQueryKey.list() })
      navigate('/classes')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fg-3">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/classes" className="inline-flex items-center gap-1 text-sm text-fg-3 hover:text-fg-1">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <p className="text-sm text-danger">Erro ao carregar classe: {extractApiError(error).message}</p>
      </div>
    )
  }

  const { class: cls, signals, goldExamples, rules } = data
  const counts = { signals: signals.length, examples: goldExamples.length, generation: rules.length }
  const maturity = classMaturity({ ruleCount: rules.length, status: cls.status })

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/classes" className="inline-flex items-center gap-1 text-sm text-fg-3 hover:text-fg-1 mb-3">
          <ArrowLeft className="h-4 w-4" /> Classes
        </Link>
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 mb-2 flex-wrap">
              <h1 className="font-display text-2xl font-bold tracking-tight text-fg-1 truncate">{cls.name}</h1>
              <StatusBadge status={cls.status} />
              <Tooltip content={maturity.description} align="start">
                <span className="inline-flex items-center gap-1.5 text-2xs font-medium text-fg-3 cursor-help">
                  <span className={cn('h-1.5 w-1.5 rounded-full', maturity.dotClass)} />
                  {maturity.label}
                </span>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2 text-xs text-fg-3">
              <span className="data-mono">{cls.slug}</span>
              <span className="text-border-strong">·</span>
              <span className="data-mono">v{cls.version}</span>
              <span className="text-border-strong">·</span>
              <span>atualizada {new Date(cls.updatedAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`Excluir a classe "${cls.name}"? Isso remove sinais, exemplos e regras.`)) {
                removeMut.mutate()
              }
            }}
            loading={removeMut.isPending}
            className="shrink-0 text-fg-3 hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
            Excluir
          </Button>
        </header>
      </div>

      <nav className="flex border-b border-border-subtle gap-0.5 -mt-1">
        {TABS.map((t) => {
          const count = t.countKey ? counts[t.countKey] : undefined
          const active = tab === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium relative transition-colors rounded-t-md',
                active ? 'text-brand-700' : 'text-fg-3 hover:text-fg-1 hover:bg-bg-subtle/60',
              )}
            >
              {t.label}
              {count != null && count > 0 && (
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-pill text-2xs data-mono font-semibold',
                    active ? 'bg-brand-50 text-brand-700' : 'bg-neutral-100 text-fg-3',
                  )}
                >
                  {count}
                </span>
              )}
              {active && <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-brand-600 rounded-pill" />}
            </button>
          )
        })}
      </nav>

      {tab === 'overview' && (
        <OverviewTab
          classId={cls.id}
          name={cls.name}
          definition={cls.definition}
          counts={counts}
          maturity={maturity}
        />
      )}
      {tab === 'signals' && <SignalsEditor classId={cls.id} signals={signals} />}
      {tab === 'examples' && <GoldExamplesEditor classId={cls.id} examples={goldExamples} />}
      {tab === 'generation' && <GenerationPanel detail={data} />}
      {tab === 'performance' && <ClassPerformanceTab classId={cls.id} className={cls.name} />}
    </div>
  )
}

// Busca precisão/cobertura mais recentes da classe reutilizando o cache de
// /benchmarks (mesmas queries da aba Performance). Custo pago uma vez por sessão.
function useLatestClassMetric(classId: string) {
  const { data: list } = useQuery({ queryKey: testSetsQueryKey.list(), queryFn: testSetsApi.list })
  const ids = (list ?? []).map((t) => t.id)
  return useQuery({
    queryKey: ['class-latest-metric', classId, ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const details = await Promise.all(ids.map((id) => testSetsApi.detail(id)))
      let best: { startedAt: string; precision: number; recall: number } | null = null
      for (const d of details) {
        for (const run of d.runs) {
          if (run.status !== 'COMPLETED' || !run.metrics) continue
          const pc = run.metrics.perClass.find((p) => p.classId === classId)
          if (!pc) continue
          if (!best || new Date(run.startedAt) > new Date(best.startedAt)) {
            best = { startedAt: String(run.startedAt), precision: pc.precision, recall: pc.recall }
          }
        }
      }
      return best
    },
  })
}

function ContextRow({
  icon: Icon,
  label,
  value,
  tip,
}: {
  icon: typeof Sparkles
  label: string
  value: string
  tip?: string
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex items-center gap-2 text-sm text-fg-3">
        <Icon className="h-3.5 w-3.5" />
        {label}
        {tip && (
          <Tooltip content={tip} align="start">
            <span className="h-1 w-1 rounded-full bg-fg-3/40 cursor-help" />
          </Tooltip>
        )}
      </span>
      <span className="data-mono text-sm font-semibold text-fg-1">{value}</span>
    </div>
  )
}

function ContextCard({
  counts,
  maturity,
  classId,
}: {
  counts: { signals: number; examples: number; generation: number }
  maturity: ReturnType<typeof classMaturity>
  classId: string
}) {
  const { data: metric, isLoading } = useLatestClassMetric(classId)
  const metricValue = (v: number | undefined) => (isLoading ? '…' : v == null ? '—' : pct(v))
  return (
    <Card className="lg:sticky lg:top-6">
      <CardHeader>
        <h3 className="font-display font-bold text-sm text-fg-1">Contexto da classe</h3>
      </CardHeader>
      <CardBody className="flex flex-col gap-3.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-fg-3">Maturidade</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-2">
            <span className={cn('h-1.5 w-1.5 rounded-full', maturity.dotClass)} />
            {maturity.label}
          </span>
        </div>
        <div className="h-px bg-border-subtle" />
        <ContextRow icon={Sparkles} label="Sinais" value={String(counts.signals)} />
        <ContextRow icon={FileText} label="Exemplos" value={String(counts.examples)} />
        <ContextRow icon={ListChecks} label="Regras" value={String(counts.generation)} />
        <div className="h-px bg-border-subtle" />
        <ContextRow
          icon={Target}
          label="Última precisão"
          value={metricValue(metric?.precision)}
          tip="Precisão da classe na run mais recente que a incluiu. Veja detalhes na aba Performance."
        />
        <ContextRow
          icon={Target}
          label="Última cobertura"
          value={metricValue(metric?.recall)}
          tip="Recall da classe: quanto dos textos esperados foram corretamente identificados na run mais recente."
        />
      </CardBody>
    </Card>
  )
}

function OverviewTab({
  classId,
  name,
  definition,
  counts,
  maturity,
}: {
  classId: string
  name: string
  definition: string
  counts: { signals: number; examples: number; generation: number }
  maturity: ReturnType<typeof classMaturity>
}) {
  const qc = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<{ name: string; definition: string }>({
    resolver: zodResolver(ClassUpdateInputSchema),
    defaultValues: { name, definition: definition ?? '' },
    values: { name, definition: definition ?? '' },
  })

  const mut = useMutation({
    mutationFn: (data: { name: string; definition: string }) => classesApi.update(classId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classesQueryKey.detail(classId) })
      qc.invalidateQueries({ queryKey: classesQueryKey.list() })
      reset(undefined, { keepValues: true, keepDirty: false })
      setSubmitError(null)
    },
    onError: (err) => setSubmitError(extractApiError(err).message),
  })

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_280px] items-start">
      <Card>
        <CardHeader>
          <h3 className="font-display font-bold text-base text-fg-1">Identidade & definição</h3>
          <p className="text-sm text-fg-3 mt-1">
            A definição vira contexto pro LLM gerar e refinar as regras desta classe.
          </p>
        </CardHeader>
        <CardBody>
          <form
            onSubmit={handleSubmit((data) => {
              setSubmitError(null)
              mut.mutate(data)
            })}
            className="flex flex-col gap-6"
            noValidate
          >
            <Field label="Nome" htmlFor="name" required error={errors.name?.message}>
              <Input id="name" invalid={!!errors.name} {...register('name')} />
            </Field>
            <Field
              label="Definição"
              htmlFor="definition"
              hint="Descreva quando uma publicação pertence a esta classe — exemplos de linguagem ajudam o LLM."
              error={errors.definition?.message}
            >
              <Textarea
                id="definition"
                rows={8}
                className="min-h-[180px] leading-relaxed"
                invalid={!!errors.definition}
                {...register('definition')}
              />
            </Field>
            {submitError && (
              <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
                {submitError}
              </div>
            )}
            <div className="flex items-center justify-end gap-3 border-t border-border-subtle pt-4">
              {isDirty && !mut.isPending && (
                <span className="inline-flex items-center gap-1.5 text-xs text-warning">
                  <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                  Alterações não salvas
                </span>
              )}
              {isDirty ? (
                <Button type="submit" loading={mut.isPending} disabled={mut.isPending}>
                  <Save className="h-4 w-4" />
                  Salvar alterações
                </Button>
              ) : (
                <Tooltip content="Nenhuma alteração para salvar" align="end">
                  <Button type="submit" disabled>
                    <Save className="h-4 w-4" />
                    Salvar alterações
                  </Button>
                </Tooltip>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      <ContextCard counts={counts} maturity={maturity} classId={classId} />
    </div>
  )
}
